import { RouteComponentProps } from 'react-router';
import { FunctionComponent, useEffect, useMemo, useState } from 'react';
import _ from 'lodash';
import toast from 'react-hot-toast';
import { Tabs, Tab, Box } from '@mui/material';
import {
  useGetCommunityQuery,
  useGetReportedContentCountQuery,
} from '../../../generated/graphql';
import auth from '../../components/auth';
import { navigate, PATHS } from '../../routes';
import { CommunityFeed } from './components/Feed';
import ReportedContent from './components/ReportedContent';
import { BlockedUsers } from './components/BlockedUsers';
import { CommunityLayout } from './components/CommunityLayout';
import { useDmFeatureEnabled } from './hooks/useDmFeatureEnabled';

type Props = { component: FunctionComponent } & RouteComponentProps<
  { id: string; influencerId: string },
  unknown
>;

const getTabs = (reportedContentCount: number) => [
  {
    label: 'Feed',
  },
  {
    label: `Reported Content (${reportedContentCount})`,
  },
  {
    label: 'Blocked Users',
  },
];

const hash = ['feed', 'reports', 'blocked'];

export const CommunityPage = (props: Props) => {
  const influencerId = parseInt(props.params.influencerId);
  const isAdminModerator = auth.isAdministrator() || auth.isModerator();
  const communityId = Number(props.params.id);
  const [tab, setTab] = useState(0);
  const { isDmFeatureEnabled } = useDmFeatureEnabled(influencerId);

  const { data: communityData } = useGetCommunityQuery({
    variables: { id: +communityId },
    fetchPolicy: 'network-only',
  });

  const { data: contentCount } = useGetReportedContentCountQuery({
    variables: { communityId: +communityId },
  });

  useEffect(() => {
    // TODO fix that hack when react-router is bumped
    if (!window.location.hash) {
      window.history.replaceState(
        null,
        '',
        window.location.pathname + '#' + hash[tab],
      );
    }
    if (window.location.hash) {
      const index = hash.findIndex(
        (i) => i === window.location.hash.substring(1),
      );
      setTab(index);
    }
  }, []);

  useEffect(() => {
    if (communityData?.community === null) {
      if (isAdminModerator) {
        navigate(PATHS.HOME);
        return;
      }
      navigate(PATHS.CREATOR_COMMUNITIES(influencerId));
    }

    if (!isAdminModerator && communityData?.community?.owner) {
      const user = auth.getUser();
      if (communityData?.community?.owner.id !== user.influencerId) {
        toast.error('Wrong community id. Try menu.');
        navigate(PATHS.HOME);
      }
    }
  }, [communityData?.community]);

  const ownerName = communityData?.community?.owner.username || 'Creator';
  const ownerProfilePicture =
    communityData?.community?.owner.profilePicture || '';
  const isUserCommunityOwner =
    communityData?.community?.owner.badges.includes('own') || false;

  const communityTabs = useMemo(
    () => getTabs(contentCount?.reportedContentCount || 0),
    [contentCount?.reportedContentCount],
  );

  const onTabChange = (
    event: React.SyntheticEvent<Element, Event>,
    newValue: number,
  ) => {
    setTab(newValue);
    window.history.replaceState(
      null,
      '',
      window.location.pathname + '#' + hash[newValue],
    );
  };

  return (
    <CommunityLayout
      communityId={+communityId}
      ownerName={ownerName}
      influencerId={influencerId}
    >
      <Box className="h-full max-w-[680px] my-0 mx-auto flex flex-col min-h-[200px]">
        <Tabs
          value={tab}
          onChange={onTabChange}
          className="mb-[30px] border-b-[#ECEDF2] border-b-[3px]"
        >
          {communityTabs.map(({ label }) => (
            <Tab key={label} label={label} />
          ))}
        </Tabs>
        {tab === 0 && (
          <CommunityFeed
            influencerId={influencerId}
            communityId={communityId}
            user={{
              name: ownerName,
              picture: ownerProfilePicture,
              isOwner: isUserCommunityOwner,
            }}
            isDmFeatureEnabled={isDmFeatureEnabled}
          />
        )}
        {tab === 1 && (
          <ReportedContent
            communityId={communityId}
            influencerId={influencerId}
          />
        )}
        {tab === 2 && <BlockedUsers communityId={communityId} />}
      </Box>
    </CommunityLayout>
  );
};
